<template>
  <ValidationProvider
    :ref="validationProviderRef"
    :mode="veeValidateInteractionMode"
    :rules="rules"
    :vid="validationProviderRef"
    :debounce="validationDelay"
    :name="label"
    :immediate="true"
  >
    <v-menu
      ref="menu"
      v-model="isMenuOpen"
      :closeOnContentClick="false"
      :returnValue.sync="mutableValue"
      transition="scale-transition"
      offsetY
      minWidth="290px"
      @input="onMenu"
    >
      <template v-slot:activator="{ on }">
        <v-text-field
          :value="date"
          :label="label"
          prependInnerIcon="event"
          :clearable="clearable"
          :color="color"
          :disabled="disabled"
          readonly
          outlined
          :dense="dense"
          :hideDetails="hideDetails"
          class="dateTimeStyle"
          :data-cy="`${dataCy}--textinput`"
          v-bind="$attrs"
          v-on="viewonly ? null : on"
        />
      </template>
      <v-date-picker
        :id="validationProviderRef"
        :ref="`${dataCy}--datepicker`"
        :value="pickerDate"
        v-bind="$attrs"
        :color="color"
        :disabled="disabled"
        :type="dateMonth ? 'month' : 'date'"
        :locale="datePickerLocales[$store.getters.userLang]"
        noTitle
        scrollable
        :range="isRange"
        :multiple="isRange"
        :landscape="landscape"
        :max="max"
        :data-cy="`${dataCy}--datepicker`"
        :showCurrent="true"
        class="datePickerOpened"
        v-on="$listeners"
        @input="sendDatas"
      />
    </v-menu>
  </ValidationProvider>
</template>

<script>
import _datePickerLocales from '@/locales/date-picker-locales';
import { ValidationProvider } from 'vee-validate';
import validationProviderHelperMixin from '@/components/shared/widgets/forms/validationHelper/validationProviderHelperMixin';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';

export default {
    components: { ValidationProvider },
    mixins: [
        validationProviderHelperMixin,
    ],
    props: {
        value: {
            type: [String, Object, Array],
            default: '',
        },
        label: {
            type: String,
            default: null,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        color: {
            type: String,
            default: '#003876',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        dateMonth: {
            type: Boolean,
            default: false,
        },
        viewonly: {
            type: Boolean,
            default: false,
        },
        isRange: {
            type: Boolean,
            default: false,
        },
        dense: {
            type: Boolean,
            default: false,
        },
        landscape: {
            type: Boolean,
            default: false,
        },
        max: {
            type: String,
            default: undefined,
        },
        format: {
            type: String,
            default: 'L',
            validation: value => ['L'].indexOf(value) !== -1,
        },
        hideDetails: {
            type: Boolean,
            default: false,
        },
        rules: {
            type: Object,
            default: () => ({ required: false }),
        },
        dataCy: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            mutableValue: this.value,
            isMenuOpen: false,
        };
    },
    computed: {
        date() {
            const values = cloneDeep(this.mutableValue || this.value);
            if (this.isRange) {
                return values.map(value => this.$options.filters.formatDate(value, this.format) || '');
            }
            if (this.dateMonth) {
                return this.$options.filters.formatDate(values, 'YYYY-MM') || '';
            }
            return this.$options.filters.formatDate(values, this.format) || '';
        },
        pickerDate() {
            const isExistedValues = this.mutableValue || this.value;
            if (!isExistedValues) return moment().format('YYYY-MM-DD');
            const values = cloneDeep(isExistedValues);
            if (this.isRange) {
                return values.map(value => moment(value).format('YYYY-MM-DD'));
            }
            if (this.dateMonth) {
                return this.onMonthSelected(values);
            }
            return moment(values).format('YYYY-MM-DD');
        },
        datePickerLocales() {
            return _datePickerLocales;
        },
    },
    watch: {
        value() {
            this.mutableValue = this.value;
        },
    },
    methods: {
        sendDatas(value) {
            this.mutableValue = value;
            /* Only if it's a date with range, the modal stay open to wait a start date and a end date */
            if (this.isRange && this.value?.length === 1) {
                if (moment(this.mutableValue[0]).isSameOrBefore(this.mutableValue[1])) {
                    // eslint-disable-next-line no-unused-expressions
                    this.$refs.menu.save(this.mutableValue) || this.$emit('input', this.mutableValue);
                } else {
                    this.$toasted.global.toast_error({ message: this.$t('generic.dateRangeError') });
                }
            } else if (!this.isRange) {
                // eslint-disable-next-line no-unused-expressions
                this.$refs.menu.save(this.dateMonth ? this.onMonthSelected(this.mutableValue) : this.mutableValue)
                || this.$emit('input', this.dateMonth ? this.onMonthSelected(this.mutableValue) : this.mutableValue);
            }
        },
        onMenu() {
            if (!this.isMenuOpen && this.value?.length) {
                // eslint-disable-next-line no-unused-expressions
                this.$refs.menu.save(this.dateMonth ? this.onMonthSelected(this.mutableValue) : this.mutableValue)
                || this.$emit('input', this.dateMonth ? this.onMonthSelected(this.mutableValue) : this.mutableValue);
            }
        },
        onMonthSelected(value) {
            const fixedDate = moment(value, 'YYYY-MM').date(3);
            return this.$options.filters.formatDate(fixedDate, 'YYYY-MM-DD');
        },
    },
};

</script>

<style lang="scss" scoped>
    .dateTimeStyle {
        width: 15rem
    }


</style>

<style>
    .datePickerOpened .v-date-picker-table {
        height: auto ;
    }
</style>
